import React, { useEffect, useMemo, useRef, useState } from "react";

const W = 980;
const H = 580;

const CDN_D3 = "https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js";
const CDN_TOPOJSON = "https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js";
const US_ATLAS = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const SCRIPT_PROMISES = new Map();

function loadScriptOnce(src) {
  if (SCRIPT_PROMISES.has(src)) return SCRIPT_PROMISES.get(src);
  const p = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      const isReady =
        existing.dataset.loaded === "1" ||
        (src.includes("d3") ? !!window.d3 : src.includes("topojson") ? !!window.topojson : false);
      if (isReady) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = () => {
      s.dataset.loaded = "1";
      resolve();
    };
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
  SCRIPT_PROMISES.set(src, p);
  return p;
}

async function ensureD3Topo() {
  await loadScriptOnce(CDN_D3);
  await loadScriptOnce(CDN_TOPOJSON);
  if (!window.d3 || !window.topojson) throw new Error("D3/TopoJSON not available");
  return { d3: window.d3, topojson: window.topojson };
}

// FIPS state ID → 2-letter code. 50 states + DC.
const FIPS_TO_CODE = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO", "09": "CT",
  "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI", "16": "ID", "17": "IL",
  "18": "IN", "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME", "24": "MD",
  "25": "MA", "26": "MI", "27": "MN", "28": "MS", "29": "MO", "30": "MT", "31": "NE",
  "32": "NV", "33": "NH", "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA", "54": "WV",
  "55": "WI", "56": "WY",
};

const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

const ALASKA_HAWAII = new Set(["AK", "HI"]);

// Default warehouse — 19446 Lansdale, PA. Module-level to keep ref stable.
const DEFAULT_WAREHOUSE = [-75.2838, 40.2415];
export const WAREHOUSE_LABEL = "19446 Lansdale, PA";

// US ports — (lng, lat, label, key). `dir` hints where the always-on label pill
// sits relative to the marker so labels don't collide on the coasts.
export const US_PORTS = [
  { key: "newark_nj", label: "Newark, NJ", lng: -74.1745, lat: 40.7357, dir: "right", st: "NJ" },
  { key: "savannah_ga", label: "Savannah, GA", lng: -81.0998, lat: 32.0809, dir: "right", st: "GA" },
  { key: "houston_tx", label: "Houston, TX", lng: -95.3698, lat: 29.7604, dir: "bottom", st: "TX" },
  { key: "los_angeles_ca", label: "Los Angeles, CA", lng: -118.2437, lat: 34.0522, dir: "right", st: "CA" },
  { key: "florida_fl", label: "Jacksonville, FL", lng: -81.6557, lat: 30.3322, dir: "rightDown", st: "FL" },
  { key: "chicago_il", label: "Chicago, IL", lng: -87.6298, lat: 41.8781, dir: "top", st: "IL" },
  { key: "montreal_ca", label: "Montreal, QC", lng: -73.5673, lat: 45.5017, dir: "top", st: null }, // Canada
];

// State code → ports in that state (only these states are interactive in
// "select" mode; the rest just show their name on hover).
const PORT_STATES = {};
US_PORTS.forEach((p) => {
  if (!p.st) return;
  if (!PORT_STATES[p.st]) PORT_STATES[p.st] = [];
  PORT_STATES[p.st].push(p);
});

// Distance zones (road miles from 19446 Lansdale, PA — our warehouse).
// Mirrors the official BidCars zone reference card 1-to-1.
export const ZONE_BY_STATE = {
  // Green: ≤ 1000 mi (Northeast & Mid-Atlantic)
  ME: "green", VT: "green", NH: "green", MA: "green", CT: "green", RI: "green",
  NY: "green", NJ: "green", PA: "green", DE: "green", MD: "green", DC: "green",
  WV: "green", VA: "green", NC: "green", SC: "green",
  // Yellow: 1000–1500 mi (Midwest & Southeast)
  MN: "yellow", WI: "yellow", MI: "yellow", IA: "yellow", IL: "yellow", IN: "yellow",
  OH: "yellow", MO: "yellow", KY: "yellow", TN: "yellow", AR: "yellow", MS: "yellow",
  AL: "yellow", GA: "yellow", FL: "yellow", LA: "yellow",
  // Red: 1500–2000 mi (Plains & South-Central)
  MT: "red", ND: "red", SD: "red", WY: "red", NE: "red", CO: "red", KS: "red",
  AZ: "red", NM: "red", OK: "red", TX: "red",
  // Gray: > 2000 mi (Far West & non-contiguous)
  WA: "gray", OR: "gray", ID: "gray", NV: "gray", UT: "gray", CA: "gray",
  AK: "gray", HI: "gray",
};

export const ZONE_PRICES = {
  green: { min: 250, max: 500, label: "≤ 1000 mi" },
  yellow: { min: 500, max: 800, label: "1000–1500 mi" },
  red: { min: 800, max: 1100, label: "1500–2000 mi" },
  gray: { min: 1100, max: 1700, label: "> 2000 mi" },
};

// On-brand sequential ramp — single sky-blue hue, light (near) → deep (far).
// Matches the site palette instead of clashing rainbow colours.
const ZONE_FILL = {
  green: "#bae6fd",  // ≤ 1000 mi
  yellow: "#7dd3fc", // 1000–1500
  red: "#38bdf8",    // 1500–2000
  gray: "#0ea5e9",   // > 2000
};
const ZONE_STROKE = {
  green: "#38bdf8",
  yellow: "#0ea5e9",
  red: "#0284c7",
  gray: "#0369a1",
};
const ZONE_DOT = {
  green: "#0ea5e9",
  yellow: "#0284c7",
  red: "#0369a1",
  gray: "#075985",
};

// Base (non-zone) map palette — neutral premium slate.
const STATE_FILL = "#eef3f9";
const STATE_FILL_HOVER = "#dbeafe";
const STATE_FILL_ACTIVE = "#bfdbfe";
const STATE_STROKE = "#cdd9e8";
const STATE_STROKE_ACTIVE = "#0284c7";

const ACCENT = "#0284c7";
const ACCENT_DARK = "#0369a1";

/**
 * USStatesMap — interactive US map using us-atlas, premium flat styling.
 *
 * Modes:
 *   "select"    – plain map, user clicks a state to pick "from" location.
 *                 Always-on port labels; the `highlightPort` is emphasized.
 *   "zones"     – states colored by distance zone from our warehouse; click
 *                 draws an arrow to the warehouse + opens an info popup.
 */
export default function USStatesMap({
  mode = "select",
  selectedState = "",
  onSelectState,
  onSelectPort,
  highlightPort = "",
  routeActive = true,
  showPorts = true,
  warehouseLngLat,
  hideInlineLegend = false,
}) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const ttRef = useRef(null);
  const [status, setStatus] = useState("loading");

  // popup for tab 2 (zones): { state, zone, x, y } in svg viewBox coords
  const [zonePopup, setZonePopup] = useState(null);
  // port chooser for a state with several ports (select mode)
  const [chooser, setChooser] = useState(null);
  const chooserOpenRef = useRef(null);
  useEffect(() => { chooserOpenRef.current = setChooser; }, []);

  const ports = useMemo(() => US_PORTS, []);
  const warehouse = warehouseLngLat || DEFAULT_WAREHOUSE;

  const onSelectStateRef = useRef(onSelectState);
  useEffect(() => { onSelectStateRef.current = onSelectState; }, [onSelectState]);
  const onSelectPortRef = useRef(onSelectPort);
  useEffect(() => { onSelectPortRef.current = onSelectPort; }, [onSelectPort]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    async function run() {
      try {
        setStatus("loading");
        const { d3, topojson } = await ensureD3Topo();
        if (cancelled || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        svg.attr("viewBox", `0 0 ${W} ${H}`);

        // ── DEFS: background gradient, soft shadows, glow ────────────────
        const defs = svg.append("defs");

        const bgGrad = defs.append("linearGradient")
          .attr("id", "us-bg").attr("x1", "0").attr("x2", "0").attr("y1", "0").attr("y2", "1");
        bgGrad.append("stop").attr("offset", "0%").attr("stop-color", "#f7fbff");
        bgGrad.append("stop").attr("offset", "100%").attr("stop-color", "#eaf1fa");

        // Subtle land sheen for the lifted-continent feel
        const landGrad = defs.append("linearGradient")
          .attr("id", "us-land").attr("x1", "0").attr("x2", "0").attr("y1", "0").attr("y2", "1");
        landGrad.append("stop").attr("offset", "0%").attr("stop-color", "#f3f7fc");
        landGrad.append("stop").attr("offset", "100%").attr("stop-color", "#e6eef8");

        // Soft drop shadow for the whole landmass (premium depth)
        const landShadow = defs.append("filter")
          .attr("id", "us-land-shadow").attr("x", "-20%").attr("y", "-20%").attr("width", "140%").attr("height", "140%");
        landShadow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "6").attr("stdDeviation", "9")
          .attr("flood-color", "#1e3a5f").attr("flood-opacity", "0.14");

        // Crisp marker shadow
        const pinShadow = defs.append("filter")
          .attr("id", "us-pin-shadow").attr("x", "-60%").attr("y", "-60%").attr("width", "220%").attr("height", "220%");
        pinShadow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "2").attr("stdDeviation", "2.4")
          .attr("flood-color", "#0f172a").attr("flood-opacity", "0.22");

        // Pill shadow
        const pillShadow = defs.append("filter")
          .attr("id", "us-pill-shadow").attr("x", "-30%").attr("y", "-30%").attr("width", "160%").attr("height", "160%");
        pillShadow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "1.5").attr("stdDeviation", "2")
          .attr("flood-color", "#1e293b").attr("flood-opacity", "0.16");

        const glow = defs.append("filter").attr("id", "us-glow").attr("x", "-60%").attr("y", "-60%").attr("width", "220%").attr("height", "220%");
        glow.append("feGaussianBlur").attr("stdDeviation", "3").attr("result", "blur");
        const gm = glow.append("feMerge");
        gm.append("feMergeNode").attr("in", "blur");
        gm.append("feMergeNode").attr("in", "SourceGraphic");

        // ── Background ──────────────────────────────────────────────────
        svg.append("rect").attr("width", W).attr("height", H).attr("fill", "url(#us-bg)");

        // Faint guide grid (very light, premium graph-paper texture)
        const grid = svg.append("g").attr("class", "us-grid").attr("opacity", 0.5);
        for (let x = 0; x <= W; x += 49) {
          grid.append("line").attr("x1", x).attr("y1", 0).attr("x2", x).attr("y2", H)
            .attr("stroke", "#dfe8f3").attr("stroke-width", 1);
        }
        for (let y = 0; y <= H; y += 49) {
          grid.append("line").attr("x1", 0).attr("y1", y).attr("x2", W).attr("y2", y)
            .attr("stroke", "#dfe8f3").attr("stroke-width", 1);
        }

        const us = await fetch(US_ATLAS, { signal: ctrl.signal }).then((r) => {
          if (!r.ok) throw new Error("us-atlas fetch failed");
          return r.json();
        });
        if (cancelled) return;

        const features = topojson.feature(us, us.objects.states).features;

        const proj = d3.geoAlbersUsa().scale(1200).translate([W / 2, H / 2 + 8]);
        const pathGen = d3.geoPath().projection(proj);

        const codeOf = (f) => FIPS_TO_CODE[String(f.id).padStart(2, "0")] || "";

        function fillFor(code) {
          if (mode === "zones") return ZONE_FILL[ZONE_BY_STATE[code]] || STATE_FILL;
          return code === selectedState ? STATE_FILL_ACTIVE : "url(#us-land)";
        }
        function strokeFor(code) {
          if (mode === "zones") {
            const z = ZONE_BY_STATE[code];
            return z ? ZONE_STROKE[z] : STATE_STROKE;
          }
          return code === selectedState ? STATE_STROKE_ACTIVE : STATE_STROKE;
        }

        // Landmass group carries the soft shadow.
        const gStates = svg.append("g")
          .attr("class", "us-map__states")
          .attr("filter", "url(#us-land-shadow)");

        gStates
          .selectAll("path")
          .data(features)
          .join("path")
          .attr("class", "us-map__state")
          .attr("data-code", (f) => codeOf(f))
          .attr("d", pathGen)
          .attr("fill", (f) => fillFor(codeOf(f)))
          .attr("stroke", (f) => strokeFor(codeOf(f)))
          .attr("stroke-width", (f) => (codeOf(f) === selectedState ? 2 : 0.9))
          .attr("stroke-linejoin", "round")
          .style("cursor", (f) => {
            const code = codeOf(f);
            if (mode === "zones") return "pointer";
            return PORT_STATES[code] ? "pointer" : "default";
          })
          .on("mouseenter", function onEnter(ev, f) {
            const code = codeOf(f);
            if (!code) return;
            const isPort = !!PORT_STATES[code];
            const tt = ttRef.current;
            // Show the name tooltip in zones mode, or for non-port states in
            // select mode (port states already carry an always-on label pill).
            if (tt && (mode === "zones" || !isPort)) {
              const zone = ZONE_BY_STATE[code];
              const zonePart =
                mode === "zones" && zone
                  ? `<div class="us-map__tt-zone us-map__tt-zone--${zone}">$${ZONE_PRICES[zone].min}–${ZONE_PRICES[zone].max} · ${ZONE_PRICES[zone].label}</div>`
                  : "";
              tt.innerHTML = `<div class="us-map__tt-title">${STATE_NAMES[code] || code}</div>${zonePart}`;
              tt.classList.add("show");
            }
            const isSelected = code === selectedState;
            if (mode === "zones") {
              d3.select(this).attr("stroke-width", 2).raise();
            } else if (PORT_STATES[code] && !isSelected) {
              // Only port-bearing states highlight in select mode.
              d3.select(this).attr("fill", STATE_FILL_HOVER).attr("stroke", "#60a5fa").attr("stroke-width", 1.6).raise();
            }
            // Non-port states in select mode: name tooltip only, no highlight.
          })
          .on("mousemove", function onMove(ev) {
            const tt = ttRef.current;
            const wrap = wrapRef.current;
            if (!tt || !wrap) return;
            const r = wrap.getBoundingClientRect();
            const tw = tt.offsetWidth || 200;
            const th = tt.offsetHeight || 60;
            let x = ev.clientX - r.left + 14;
            let y = ev.clientY - r.top - th / 2;
            if (x + tw > r.width - 8) x = ev.clientX - r.left - tw - 14;
            if (y < 8) y = 8;
            if (y + th > r.height - 8) y = r.height - th - 8;
            tt.style.left = `${x}px`;
            tt.style.top = `${y}px`;
          })
          .on("mouseleave", function onLeave(ev, f) {
            const tt = ttRef.current;
            if (tt) tt.classList.remove("show");
            const code = codeOf(f);
            d3.select(this)
              .attr("fill", fillFor(code))
              .attr("stroke", strokeFor(code))
              .attr("stroke-width", code === selectedState ? 2 : 0.9);
          })
          .on("click", function onClick(ev, f) {
            const code = codeOf(f);
            if (!code) return;
            const cb = onSelectStateRef.current;
            if (typeof cb === "function") cb(code);
            if (mode === "zones") {
              const c = pathGen.centroid(f);
              if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
                setZonePopup({ state: code, zone: ZONE_BY_STATE[code] || null, x: c[0], y: c[1] });
              }
              return;
            }
            // select mode: click a port-state → pick its port (or choose if many)
            const sp = PORT_STATES[code];
            if (!sp || !sp.length) return;
            if (sp.length === 1) {
              onSelectPortRef.current?.(sp[0].key);
              chooserOpenRef.current?.(null);
              return;
            }
            const c = pathGen.centroid(f);
            if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
              chooserOpenRef.current?.({ ports: sp, x: c[0], y: c[1], title: STATE_NAMES[code] || code });
            }
          });

        // ── Animated shipping route: auction state → highlighted port ────
        // (the actual land leg "pickup state → US port"). Falls back to the
        // warehouse as the start point when no auction state is known.
        if (showPorts && mode === "select" && highlightPort && routeActive) {
          const hp = ports.find((p) => p.key === highlightPort);
          const ppt = hp ? proj([hp.lng, hp.lat]) : null;
          let spt = null;
          if (selectedState) {
            const sf = features.find((f) => codeOf(f) === selectedState);
            if (sf) {
              const c = pathGen.centroid(sf);
              if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) spt = c;
            }
          }
          // No US pickup state (e.g. Canadian origin) → don't draw a route line;
          // we don't have the origin's coordinates, so a guess would mislead.
          if (spt && ppt) {
            const dx = ppt[0] - spt[0], dy = ppt[1] - spt[1];
            const dist = Math.hypot(dx, dy) || 1;
            // bow the line along the left normal for a gentle arc
            const mx = (spt[0] + ppt[0]) / 2 + (-dy / dist) * dist * 0.16;
            const my = (spt[1] + ppt[1]) / 2 + (dx / dist) * dist * 0.16;
            const d = `M ${spt[0]} ${spt[1]} Q ${mx} ${my} ${ppt[0]} ${ppt[1]}`;
            const routeLayer = svg.append("g").attr("class", "us-map__route-layer");
            routeLayer.append("path").attr("class", "us-map__route-base").attr("d", d);
            routeLayer.append("path").attr("class", "us-map__route-flow").attr("d", d);
            // Origin dot on the auction state (the pickup point).
            if (selectedState) {
              routeLayer.append("circle")
                .attr("cx", spt[0]).attr("cy", spt[1]).attr("r", 5.5)
                .attr("fill", "#16a34a").attr("stroke", "#fff").attr("stroke-width", 2)
                .attr("filter", "url(#us-pin-shadow)");
            }
          }
        }

        // ── Warehouse marker (dark pill + glow) ──────────────────────────
        if (mode === "zones" || mode === "select") {
          const wpt = proj(warehouse);
          if (wpt) {
            const wg = svg.append("g")
              .attr("class", "us-map__warehouse")
              .attr("transform", `translate(${wpt[0]},${wpt[1]})`);
            wg.append("circle")
              .attr("class", "us-map__pulse")
              .attr("r", 13).attr("fill", ACCENT).attr("fill-opacity", 0.3).attr("filter", "url(#us-glow)");
            wg.append("circle").attr("r", 17).attr("fill", ACCENT).attr("fill-opacity", 0.12);
            wg.append("circle").attr("r", 6.5).attr("fill", ACCENT).attr("stroke", "#fff").attr("stroke-width", 2.5).attr("filter", "url(#us-pin-shadow)");

            // Dark hero pill, offset up-left to clear the Newark label.
            drawPill(wg, {
              text: WAREHOUSE_LABEL, dir: "topLeft", hero: true, dotColor: "#60a5fa",
            });
          }
        }

        // ── Port markers + always-on labels (select mode) ────────────────
        if (showPorts && mode === "select") {
          const portLayer = svg.append("g").attr("class", "us-map__ports");
          ports.forEach((p) => {
            const pt = proj([p.lng, p.lat]);
            if (!pt) return;
            const isHi = highlightPort === p.key;
            const g = portLayer.append("g")
              .attr("class", `us-map__port${isHi ? " is-active" : ""}`)
              .attr("transform", `translate(${pt[0]},${pt[1]})`)
              .style("cursor", "pointer")
              .on("click", () => {
                const cb = onSelectPortRef.current;
                if (typeof cb === "function") cb(p.key);
              });
            if (isHi) g.raise();

            // Invisible larger hit area for easier clicking.
            g.append("circle").attr("r", 15).attr("fill", "transparent");

            if (isHi) {
              g.append("circle")
                .attr("class", "us-map__pulse")
                .attr("r", 12).attr("fill", ACCENT).attr("fill-opacity", 0.45).attr("filter", "url(#us-glow)");
            }
            g.append("circle")
              .attr("r", isHi ? 8 : 6)
              .attr("fill", "#fff")
              .attr("filter", "url(#us-pin-shadow)");
            g.append("circle")
              .attr("r", isHi ? 5 : 3.6)
              .attr("fill", isHi ? ACCENT_DARK : ACCENT);

            drawPill(g, {
              text: p.label, dir: p.dir || "right", active: isHi, dotColor: isHi ? "#bae6fd" : ACCENT,
            });
          });
        }

        setStatus("ok");
      } catch (e) {
        if (!cancelled) setStatus("err");
      }
    }

    run();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [mode, selectedState, highlightPort, routeActive, showPorts, ports, warehouse]);

  const warehouseVB = useMemo(() => {
    if (!window.d3) return null;
    const proj = window.d3.geoAlbersUsa().scale(1200).translate([W / 2, H / 2 + 8]);
    return proj(warehouse);
  }, [warehouse, status]);

  return (
    <div className={`us-map us-map--${mode}`} ref={wrapRef}>
      {status === "loading" && (
        <div className="us-map__loading"><span className="us-map__spinner" />Loading map…</div>
      )}
      {status === "err" && (
        <div className="us-map__error">Map failed to load.</div>
      )}
      <svg ref={svgRef} className="us-map__svg" viewBox={`0 0 ${W} ${H}`} />
      {mode === "zones" && zonePopup && warehouseVB && (
        <svg className="us-map__overlay" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          <defs>
            <marker id="us-arrow-head" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 Z" fill={ACCENT} />
            </marker>
          </defs>
          <path
            d={`M ${zonePopup.x} ${zonePopup.y} L ${warehouseVB[0]} ${warehouseVB[1]}`}
            stroke={ACCENT} strokeWidth="2.5" strokeDasharray="6 4" fill="none"
            markerEnd="url(#us-arrow-head)" opacity="0.9"
          />
        </svg>
      )}
      <div ref={ttRef} className="us-map__tt" />
      {mode === "zones" && zonePopup && (
        <ZonePopup popup={zonePopup} onClose={() => setZonePopup(null)} />
      )}
      {chooser && (
        <div
          className="eu-map__chooser"
          style={{ left: `${(chooser.x / W) * 100}%`, top: `${(chooser.y / H) * 100}%` }}
          role="dialog"
        >
          <button type="button" className="eu-map__chooser-close" onClick={() => setChooser(null)} aria-label="Close">×</button>
          <div className="eu-map__chooser-title">{chooser.title}</div>
          <div className="eu-map__chooser-sub">Choose US port</div>
          <div className="eu-map__chooser-list">
            {chooser.ports.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`eu-map__chooser-port${p.key === highlightPort ? " is-active" : ""}`}
                onClick={() => { onSelectPort?.(p.key); setChooser(null); }}
              >
                <span className="eu-map__chooser-anchor" aria-hidden="true">⚓</span>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {mode === "zones" && !hideInlineLegend && (
        <div className="us-map__legend">
          <div className="us-map__legend-title">Distance · price</div>
          {Object.entries(ZONE_PRICES).map(([k, v]) => (
            <div key={k} className="us-map__legend-row">
              <span className="us-map__legend-dot" style={{ background: ZONE_FILL[k], borderColor: ZONE_STROKE[k] }} />
              <span className="us-map__legend-label">{v.label} · ${v.min}–{v.max}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * drawPill — premium always-on label. `dir` controls placement around the
 * marker so coastal labels don't overlap.
 */
function drawPill(parent, { text, dir = "right", active = false, hero = false }) {
  const padX = 12;
  const charW = 6.5;
  const tw = Math.round(text.length * charW + padX * 2);
  const th = 22;
  const gap = 13; // distance from marker centre to the pill edge

  let x = 0, y = 0; // pill centre
  switch (dir) {
    case "left":
      x = -(gap + tw / 2); y = 0; break;
    case "top":
      x = 0; y = -(gap + th / 2); break;
    case "bottom":
      x = 0; y = gap + th / 2; break;
    case "topLeft":
      x = -(gap + tw / 2) * 0.7; y = -(gap + th); break;
    case "rightDown":
      x = gap + tw / 2; y = 14; break;
    case "right":
    default:
      x = gap + tw / 2; y = 0; break;
  }

  const g = parent.append("g").attr("transform", `translate(${x},${y})`);

  const bg = hero ? "#0f172a" : active ? "#0284c7" : "#ffffff";
  const fg = hero || active ? "#ffffff" : "#0f172a";
  const stroke = hero ? "#0f172a" : active ? "#0284c7" : "#e2e8f0";

  g.append("rect")
    .attr("x", -tw / 2).attr("y", -th / 2)
    .attr("width", tw).attr("height", th).attr("rx", 11)
    .attr("fill", bg).attr("stroke", stroke).attr("stroke-width", 1)
    .attr("filter", "url(#us-pill-shadow)");

  g.append("text")
    .attr("text-anchor", "middle").attr("x", 0).attr("y", 0).attr("dy", "0.34em")
    .attr("font-size", hero ? "11.5px" : "11px")
    .attr("font-weight", "700")
    .attr("fill", fg)
    .text(text);

  return g;
}

function ZonePopup({ popup, onClose }) {
  const { state, zone, x, y } = popup;
  // Flip below / shift sideways so the popup never clips the map edges.
  const flipDown = y < H * 0.34;
  const nearLeft = x < W * 0.2;
  const nearRight = x > W * 0.8;
  const tx = nearLeft ? "-10%" : nearRight ? "-90%" : "-50%";
  const ty = flipDown ? "16%" : "-110%";
  const left = `${(x / W) * 100}%`;
  const top = `${(y / H) * 100}%`;
  const z = zone ? ZONE_PRICES[zone] : null;
  const cls = `us-map__popup${zone ? ` us-map__popup--${zone}` : ""}` +
    `${flipDown ? " us-map__popup--below" : ""}${(nearLeft || nearRight) ? " us-map__popup--noarrow" : ""}`;
  return (
    <div
      className={cls}
      style={{ left, top, transform: `translate(${tx}, ${ty})` }}
      role="dialog"
    >
      <button type="button" className="us-map__popup-close" onClick={onClose} aria-label="Close">×</button>
      <div className="us-map__popup-state">{STATE_NAMES[state] || state}</div>
      {z ? (
        <>
          <div className="us-map__popup-zone">{z.label}</div>
          <div className="us-map__popup-price">${z.min}–${z.max}</div>
          <div className="us-map__popup-hint">Truck to {WAREHOUSE_LABEL}</div>
        </>
      ) : ALASKA_HAWAII.has(state) ? (
        <div className="us-map__popup-hint">Quote on request</div>
      ) : (
        <div className="us-map__popup-hint">No zone data</div>
      )}
    </div>
  );
}
